import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Barcode from "react-barcode";
import {
  ArrowLeft,
  ImagePlus,
  Loader2,
  Package,
  Plus,
  Trash2,
  Wand2,
  X,
} from "lucide-react";

import DashboardLayout from "src/components/layout/dashboard/Layout";
import Card from "src/components/common/card/Card";
import Button from "src/components/ui/Button";
import Input from "src/components/ui/Input";
import Textarea from "src/components/ui/Textarea";
import FilterSelect from "src/components/ui/FilterSelect";

import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import * as productActions from "src/redux/slices/productSlice";
import * as categoryActions from "src/redux/slices/categorySlice";
import * as brandActions from "src/redux/slices/brandSlice";
import * as unitActions from "src/redux/slices/unitSlice";
import { toast } from "react-toastify";

const GST_OPTIONS = [
  { label: "0% (Exempt)", value: "0" },
  { label: "5%", value: "5" },
  { label: "12%", value: "12" },
  { label: "18%", value: "18" },
  { label: "28%", value: "28" },
];

const generateBarcodeValue = () =>
  Array.from({ length: 12 }, () => Math.floor(Math.random() * 10)).join("");

interface VariantOption {
  name: string;
  values: string[];
}

interface VariantRow {
  optionValues: Record<string, string>;
  sku: string;
  barcode: string;
  buyingPrice: string;
  sellingPrice: string;
  mrp: string;
  minStock: string;
}

// One row per combination of every option's values, keyed by option
// name -> value so edits survive re-generating the combination list.
const comboKey = (optionValues: Record<string, string>, options: VariantOption[]) =>
  options.map((opt) => `${opt.name}:${optionValues[opt.name] ?? ""}`).join("|");

const buildCombinations = (options: VariantOption[]): Record<string, string>[] => {
  const valid = options.filter((opt) => opt.name.trim() && opt.values.length);
  if (!valid.length) return [];
  return valid.reduce<Record<string, string>[]>(
    (acc, opt) => {
      const next: Record<string, string>[] = [];
      for (const combo of acc) {
        for (const value of opt.values) {
          next.push({ ...combo, [opt.name]: value });
        }
      }
      return next;
    },
    [{}]
  );
};

const emptyVariantRow = (optionValues: Record<string, string>): VariantRow => ({
  optionValues,
  sku: "",
  barcode: "",
  buyingPrice: "",
  sellingPrice: "",
  mrp: "",
  minStock: "",
});

/** Section header with the numbered badge used throughout this form. */
const SectionCard: React.FC<{
  step: number;
  title: string;
  description?: string;
  actions?: React.ReactNode;
  children: React.ReactNode;
}> = ({ step, title, description, actions, children }) => (
  <Card>
    <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
      <div className="flex items-start gap-3">
        <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary-100 text-xs font-semibold text-primary-700 dark:bg-primary-900/40 dark:text-primary-300">
          {step}
        </span>
        <div>
          <h3 className="font-bold text-gray-900 dark:text-gray-100">{title}</h3>
          {description && (
            <p className="text-sm text-gray-500 dark:text-gray-400">{description}</p>
          )}
        </div>
      </div>
      {actions}
    </div>
    {children}
  </Card>
);

/** Barcode input + generate button, with a live preview once a value exists. */
const BarcodeField: React.FC<{
  value: string;
  onChange: (value: string) => void;
  compact?: boolean;
}> = ({ value, onChange, compact = false }) => (
  <div>
    <div className="flex items-center gap-2">
      <Input
        placeholder="Barcode value"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={compact ? "w-32" : "flex-1"}
      />
      <Button
        variant="outline"
        rounded="full"
        size={compact ? "sm" : "md"}
        className="h-9 shrink-0"
        onClick={() => onChange(generateBarcodeValue())}
        title="Generate barcode"
      >
        <Wand2 size={compact ? 13 : 15} className={compact ? "" : "mr-1.5"} />
        {!compact && "Generate"}
      </Button>
    </div>
    {value && (
      <div
        className={`mt-2 inline-block rounded-lg border border-gray-200 bg-white p-2 dark:border-gray-700 ${
          compact ? "" : "p-3"
        }`}
      >
        <Barcode
          value={value}
          format="CODE128"
          height={compact ? 24 : 50}
          width={compact ? 1 : 1.6}
          fontSize={compact ? 8 : 13}
          margin={0}
          background="#ffffff"
          lineColor="#111827"
        />
      </div>
    )}
  </div>
);

/** Free-text "type + Enter" input for adding a value chip to an option. */
const OptionValueInput: React.FC<{ onAdd: (value: string) => void }> = ({ onAdd }) => {
  const [draft, setDraft] = useState("");
  const commit = () => {
    if (draft.trim()) {
      onAdd(draft.trim());
      setDraft("");
    }
  };
  return (
    <div className="flex items-center gap-2">
      <Input
        placeholder="Type a value (e.g. S) and press Enter"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === ",") {
            e.preventDefault();
            commit();
          }
        }}
        className="flex-1"
      />
      <Button
        variant="outline"
        rounded="full"
        size="sm"
        className="h-9 shrink-0"
        onClick={commit}
      >
        <Plus size={14} className="mr-1" />
        Add
      </Button>
    </div>
  );
};

const ProductForm: React.FC = () => {
  const { productId } = useParams();
  const isEdit = !!productId;
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const { createProduct, editProduct, getProduct } = useAppSelector(
    (state) => state.product
  );
  const { categoryList } = useAppSelector((state) => state.category);
  const { brandList } = useAppSelector((state) => state.brand);
  const { unitList } = useAppSelector((state) => state.unit);

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [subCategoryId, setSubCategoryId] = useState("");
  const [brandId, setBrandId] = useState("");
  const [unitId, setUnitId] = useState("");
  const [productType, setProductType] = useState<"single" | "variant">("single");

  // Single-product mode
  const [barcode, setBarcode] = useState("");
  const [sku, setSku] = useState("");
  const [buyingPrice, setBuyingPrice] = useState("");
  const [sellingPrice, setSellingPrice] = useState("");
  const [minStock, setMinStock] = useState("");

  // Variant mode
  const [variantOptions, setVariantOptions] = useState<VariantOption[]>([]);
  const [variants, setVariants] = useState<VariantRow[]>([]);

  const [gstRate, setGstRate] = useState("18");
  const [hsnCode, setHsnCode] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});

  const loading = createProduct.loading || editProduct.loading;

  // Fetch reference data once
  useEffect(() => {
    dispatch(categoryActions.getCategoryListRequest({ page: 1, limit: 200 }));
    dispatch(brandActions.getBrandListRequest({ page: 1, limit: 200, status: "active" }));
    dispatch(unitActions.getUnitListRequest({ page: 1, limit: 200, status: "active" }));
  }, [dispatch]);

  // Fetch-and-prepopulate for edit mode
  useEffect(() => {
    if (productId) dispatch(productActions.getProductRequest(productId));
    return () => {
      dispatch(productActions.resetGetProduct());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId]);

  useEffect(() => {
    const p: any = getProduct.data?.data;
    if (!p) return;
    setName(p.name || "");
    setDescription(p.description || "");
    setCategoryId(typeof p.categoryId === "string" ? p.categoryId : p.categoryId?._id || "");
    setSubCategoryId(
      typeof p.subCategoryId === "string" ? p.subCategoryId : p.subCategoryId?._id || ""
    );
    setBrandId(typeof p.brandId === "string" ? p.brandId : p.brandId?._id || "");
    setUnitId(typeof p.unitId === "string" ? p.unitId : p.unitId?._id || "");
    setProductType(p.productType === "variant" ? "variant" : "single");
    setBarcode(p.barcode || "");
    setSku(p.sku || "");
    setBuyingPrice(p.buyingPrice != null ? String(p.buyingPrice) : "");
    setSellingPrice(p.sellingPrice != null ? String(p.sellingPrice) : "");
    setMinStock(p.minStock != null ? String(p.minStock) : "");
    setGstRate(p.gstRate != null ? String(p.gstRate) : "18");
    setHsnCode(p.hsnCode || "");
    setVariantOptions(
      (p.variantOptions || []).map((o: any) => ({ name: o.name || "", values: o.values || [] }))
    );
    setVariants(
      (p.variants || []).map((v: any) => ({
        optionValues: v.optionValues || {},
        sku: v.sku || "",
        barcode: v.barcode || "",
        buyingPrice: v.buyingPrice != null ? String(v.buyingPrice) : "",
        sellingPrice: v.sellingPrice != null ? String(v.sellingPrice) : "",
        mrp: v.mrp != null ? String(v.mrp) : "",
        minStock: v.minStock != null ? String(v.minStock) : "",
      }))
    );
  }, [getProduct.data]);

  // Success/error side-effects
  useEffect(() => {
    if (createProduct.data) {
      toast.success("Product created successfully");
      handleClose();
    } else if (createProduct.error) {
      toast.error(`Error: ${createProduct.error}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createProduct]);

  useEffect(() => {
    if (editProduct.data) {
      toast.success("Product updated successfully");
      handleClose();
    } else if (editProduct.error) {
      toast.error(`Error: ${editProduct.error}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editProduct]);

  const handleClose = () => {
    dispatch(productActions.resetCreateProduct());
    dispatch(productActions.resetEditProduct());
    navigate("/app/product/list");
  };

  const categories = categoryList?.data?.data || [];
  const selectedCategory: any = categories.find((c: any) => c._id === categoryId);
  const subCategoryOptions: any[] = (
    selectedCategory?.subCategories ||
    selectedCategory?.subcategories ||
    []
  ).filter((s: any) => s.status !== "inactive");

  const handleCategoryChange = (val: string) => {
    setCategoryId(val);
    setSubCategoryId("");
  };

  // Keep the variant table in sync with the option/value editor, merging by
  // combination key so already-entered SKU/price data isn't lost when
  // another option or value is added elsewhere.
  const combinations = useMemo(() => buildCombinations(variantOptions), [variantOptions]);
  useEffect(() => {
    setVariants((prev) => {
      const prevMap = new Map(
        prev.map((row) => [comboKey(row.optionValues, variantOptions), row])
      );
      return combinations.map((combo) => {
        const key = comboKey(combo, variantOptions);
        return prevMap.get(key) || emptyVariantRow(combo);
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(combinations)]);

  const addOption = () =>
    setVariantOptions((prev) => [...prev, { name: "", values: [] }]);
  const removeOption = (idx: number) =>
    setVariantOptions((prev) => prev.filter((_, i) => i !== idx));
  const updateOptionName = (idx: number, name: string) =>
    setVariantOptions((prev) => prev.map((o, i) => (i === idx ? { ...o, name } : o)));
  const addOptionValue = (idx: number, value: string) =>
    setVariantOptions((prev) =>
      prev.map((o, i) =>
        i === idx && !o.values.includes(value) ? { ...o, values: [...o.values, value] } : o
      )
    );
  const removeOptionValue = (idx: number, value: string) =>
    setVariantOptions((prev) =>
      prev.map((o, i) => (i === idx ? { ...o, values: o.values.filter((v) => v !== value) } : o))
    );

  const updateVariantField = (key: string, field: keyof VariantRow, value: string) =>
    setVariants((prev) =>
      prev.map((row) =>
        comboKey(row.optionValues, variantOptions) === key ? { ...row, [field]: value } : row
      )
    );

  const validate = () => {
    const e: Record<string, string> = {};
    if (!name.trim()) e.name = "Product name is required";
    if (!categoryId) e.categoryId = "Category is required";
    if (productType === "single") {
      if (!sellingPrice || Number(sellingPrice) <= 0) {
        e.sellingPrice = "Selling price is required";
      }
    } else {
      const validOptions = variantOptions.filter((o) => o.name.trim() && o.values.length);
      if (!validOptions.length) e.options = "Add at least one option with values";
      if (!variants.length) e.variants = "No variants generated yet";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) {
      toast.error("Please fix the highlighted fields");
      return;
    }

    const payload: any = {
      name: name.trim(),
      description: description.trim() || undefined,
      categoryId,
      subCategoryId: subCategoryId || undefined,
      brandId: brandId || undefined,
      unitId: unitId || undefined,
      productType,
      gstRate: Number(gstRate),
      hsnCode: hsnCode.trim() || undefined,
    };

    if (productType === "single") {
      payload.sku = sku.trim() || undefined;
      payload.barcode = barcode.trim() || undefined;
      payload.buyingPrice = buyingPrice !== "" ? Number(buyingPrice) : undefined;
      payload.sellingPrice = Number(sellingPrice);
      payload.minStock = minStock !== "" ? Number(minStock) : undefined;
    } else {
      payload.variantOptions = variantOptions.filter(
        (o) => o.name.trim() && o.values.length
      );
      payload.variants = variants.map((v) => ({
        optionValues: v.optionValues,
        sku: v.sku.trim() || undefined,
        barcode: v.barcode.trim() || undefined,
        buyingPrice: v.buyingPrice !== "" ? Number(v.buyingPrice) : undefined,
        sellingPrice: v.sellingPrice !== "" ? Number(v.sellingPrice) : undefined,
        mrp: v.mrp !== "" ? Number(v.mrp) : undefined,
        minStock: v.minStock !== "" ? Number(v.minStock) : undefined,
      }));
    }

    if (isEdit) {
      dispatch(productActions.editProductRequest({ ...payload, _id: productId }));
    } else {
      dispatch(productActions.createProductRequest(payload));
    }
  };

  if (isEdit && getProduct.loading && !getProduct.data) {
    return (
      <DashboardLayout
        meta={{ title: "Edit Product | Invyntra", description: "Edit product." }}
      >
        <div className="flex items-center justify-center gap-2 py-24 text-gray-500 dark:text-gray-400">
          <Loader2 size={18} className="animate-spin" />
          Loading product…
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout
      meta={{
        title: isEdit ? "Edit Product | Invyntra" : "Add Product | Invyntra",
        description: "Create or edit products.",
        keywords: "products, inventory, invyntra",
      }}
    >
      <div className="space-y-4 pb-4">
        {/* Header */}
        <div className="flex items-start gap-4">
          <button
            type="button"
            onClick={() => navigate("/app/product/list")}
            className="mt-1 flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800"
            title="Back to products"
          >
            <ArrowLeft size={18} />
          </button>
          <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-primary-100 text-primary-600 dark:bg-primary-900/40 dark:text-primary-300">
            <Package size={26} />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              {isEdit ? "Edit Product" : "Add Product"}
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {isEdit
                ? "Update this product's details."
                : "Fill in the details to add a new product to your catalog."}
            </p>
          </div>
        </div>

        {/* 1. Basic information */}
        <SectionCard step={1} title="Basic information">
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            <div className="space-y-4 lg:col-span-2">
              <Input
                label="Product name"
                placeholder="e.g. Cotton T-Shirt"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                error={!!errors.name}
                helperText={errors.name}
              />

              <Textarea
                label="Description"
                optional
                placeholder="What makes this product unique…"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <FilterSelect
                  variant="boxed"
                  label="Category"
                  value={categoryId}
                  onChange={handleCategoryChange}
                  allLabel="None"
                  required
                  error={!!errors.categoryId}
                  helperText={errors.categoryId}
                  options={categories.map((c: any) => ({ label: c.name, value: c._id }))}
                />

                {subCategoryOptions.length > 0 && (
                  <FilterSelect
                    variant="boxed"
                    label="Subcategory"
                    value={subCategoryId}
                    onChange={setSubCategoryId}
                    allLabel="None"
                    options={subCategoryOptions.map((s: any) => ({
                      label: s.name,
                      value: s._id,
                    }))}
                  />
                )}

                <FilterSelect
                  variant="boxed"
                  label="Brand"
                  value={brandId}
                  onChange={setBrandId}
                  allLabel="None"
                  options={(brandList?.data?.data || []).map((b: any) => ({
                    label: b.name,
                    value: b._id,
                  }))}
                />

                <FilterSelect
                  variant="boxed"
                  label="Unit"
                  value={unitId}
                  onChange={setUnitId}
                  allLabel="None"
                  options={(unitList?.data?.data || []).map((u: any) => ({
                    label: u.shortName ? `${u.name} (${u.shortName})` : u.name,
                    value: u._id,
                  }))}
                />
              </div>
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Catalog image
              </label>
              <p className="mb-2 text-xs text-gray-500 dark:text-gray-400">
                Used for product cards and listings.
              </p>
              <div className="flex h-40 flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-gray-300 bg-gray-50 px-4 text-center dark:border-gray-700 dark:bg-gray-800/60">
                <ImagePlus size={22} className="text-gray-400 dark:text-gray-500" />
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {isEdit
                    ? "Catalog images aren't supported yet."
                    : "Save the product first, then you can upload a catalog image here."}
                </p>
              </div>
              <Button variant="outline" fullWidth className="mt-2" disabled>
                Upload image
              </Button>
            </div>
          </div>
        </SectionCard>

        {/* 2. Product type */}
        <SectionCard
          step={2}
          title="Product type"
          description="Choose whether this product has variants (e.g. size, color) or a single SKU."
        >
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <button
              type="button"
              onClick={() => setProductType("single")}
              className={`rounded-xl border p-4 text-left transition ${
                productType === "single"
                  ? "border-primary-500 ring-1 ring-primary-500 bg-primary-50/50 dark:bg-primary-900/20"
                  : "border-gray-200 hover:border-gray-300 dark:border-gray-700 dark:hover:border-gray-600"
              }`}
            >
              <div className="font-semibold text-gray-900 dark:text-gray-100">
                Single product
              </div>
              <div className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                One SKU, one price. No size/color options.
              </div>
            </button>

            <button
              type="button"
              onClick={() => setProductType("variant")}
              className={`rounded-xl border p-4 text-left transition ${
                productType === "variant"
                  ? "border-primary-500 ring-1 ring-primary-500 bg-primary-50/50 dark:bg-primary-900/20"
                  : "border-gray-200 hover:border-gray-300 dark:border-gray-700 dark:hover:border-gray-600"
              }`}
            >
              <div className="font-semibold text-gray-900 dark:text-gray-100">
                Product with variants
              </div>
              <div className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                Multiple options (e.g. Size: S, M, L and Color).
              </div>
            </button>
          </div>
        </SectionCard>

        {productType === "single" ? (
          <>
            {/* 3. Barcode (single) */}
            <SectionCard
              step={3}
              title="Barcode"
              description="Generate a scannable barcode for single-product mode."
            >
              <BarcodeField value={barcode} onChange={setBarcode} />
            </SectionCard>

            {/* 4. Price & stock (single) */}
            <SectionCard step={4} title="Price & stock">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <Input
                  label="SKU"
                  optional
                  placeholder="e.g. TSHIRT-BLK-M"
                  value={sku}
                  onChange={(e) => setSku(e.target.value)}
                />
                <Input
                  label="Buying price"
                  optional
                  type="number"
                  min={0}
                  placeholder="0.00"
                  value={buyingPrice}
                  onChange={(e) => setBuyingPrice(e.target.value)}
                />
                <Input
                  label="Selling price"
                  type="number"
                  min={0}
                  placeholder="0.00"
                  value={sellingPrice}
                  onChange={(e) => setSellingPrice(e.target.value)}
                  required
                  error={!!errors.sellingPrice}
                  helperText={errors.sellingPrice}
                />
                <Input
                  label="Min quantity"
                  optional
                  type="number"
                  min={0}
                  placeholder="10"
                  value={minStock}
                  onChange={(e) => setMinStock(e.target.value)}
                />
              </div>
            </SectionCard>
          </>
        ) : (
          <>
            {/* 3. Options (variant) */}
            <SectionCard
              step={3}
              title="Add options"
              description="Each option is a dimension (e.g. Size, Color). Values combine into every SKU below."
              actions={
                combinations.length > 0 ? (
                  <span className="rounded-full bg-primary-50 px-3 py-1 text-xs font-medium text-primary-700 dark:bg-primary-900/40 dark:text-primary-300">
                    {combinations.length} variant{combinations.length === 1 ? "" : "s"} from
                    combinations
                  </span>
                ) : undefined
              }
            >
              <div className="space-y-4">
                <div className="flex flex-wrap gap-4">
                  {variantOptions.map((opt, idx) => (
                    <div
                      key={idx}
                      className="min-w-[300px] flex-1 basis-[320px] space-y-3 rounded-xl border border-gray-200 p-4 dark:border-gray-700"
                    >
                    <div className="flex items-center gap-3">
                      <Input
                        label="Option name"
                        placeholder="e.g. Size"
                        value={opt.name}
                        onChange={(e) => updateOptionName(idx, e.target.value)}
                        className="flex-1"
                      />
                      <button
                        type="button"
                        onClick={() => removeOption(idx)}
                        className="mt-6 shrink-0 rounded-full p-2 text-gray-400 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/30 dark:hover:text-red-400"
                        title="Remove option"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>

                    <div>
                      <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Values
                      </label>
                      {opt.values.length > 0 && (
                        <div className="mb-2 flex flex-wrap gap-2">
                          {opt.values.map((v) => (
                            <span
                              key={v}
                              className="flex items-center gap-1 rounded-full bg-gray-100 px-3 py-1 text-sm text-gray-700 dark:bg-gray-700 dark:text-gray-200"
                            >
                              {v}
                              <button
                                type="button"
                                onClick={() => removeOptionValue(idx, v)}
                                className="text-gray-400 hover:text-red-600 dark:hover:text-red-400"
                              >
                                <X size={12} />
                              </button>
                            </span>
                          ))}
                        </div>
                      )}
                      <OptionValueInput onAdd={(value) => addOptionValue(idx, value)} />
                    </div>
                  </div>
                  ))}
                </div>

                {errors.options && (
                  <p className="text-sm text-red-500">{errors.options}</p>
                )}

                <Button variant="outline" rounded="full" onClick={addOption}>
                  <Plus size={16} className="mr-1.5" />
                  Add option
                </Button>
              </div>
            </SectionCard>

            {/* 4. Variants table */}
            <SectionCard
              step={4}
              title="Variants"
              description="Fill in SKU, barcode, and pricing for each combination."
            >
              {variants.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Add at least one option with values to generate variant rows.
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full min-w-max text-left text-sm">
                    <thead className="border-b border-gray-100 text-xs text-gray-500 dark:border-gray-700 dark:text-gray-400">
                      <tr>
                        {variantOptions
                          .filter((o) => o.name.trim())
                          .map((opt) => (
                            <th key={opt.name} className="py-2 pr-4 font-medium">
                              {opt.name}
                            </th>
                          ))}
                        <th className="py-2 pr-4 font-medium">SKU</th>
                        <th className="py-2 pr-4 font-medium">Barcode</th>
                        <th className="py-2 pr-4 font-medium">Buying price</th>
                        <th className="py-2 pr-4 font-medium">Selling price</th>
                        <th className="py-2 pr-4 font-medium">MRP</th>
                        <th className="py-2 pr-4 font-medium">Min qty</th>
                      </tr>
                    </thead>
                    <tbody>
                      {variants.map((row) => {
                        const key = comboKey(row.optionValues, variantOptions);
                        return (
                          <tr
                            key={key}
                            className="border-b border-gray-50 align-top last:border-b-0 dark:border-gray-800"
                          >
                            {variantOptions
                              .filter((o) => o.name.trim())
                              .map((opt) => (
                                <td key={opt.name} className="py-3 pr-4 text-gray-700 dark:text-gray-300">
                                  {row.optionValues[opt.name]}
                                </td>
                              ))}
                            <td className="py-3 pr-4">
                              <Input
                                value={row.sku}
                                onChange={(e) => updateVariantField(key, "sku", e.target.value)}
                                className="w-32"
                              />
                            </td>
                            <td className="py-3 pr-4">
                              <BarcodeField
                                compact
                                value={row.barcode}
                                onChange={(v) => updateVariantField(key, "barcode", v)}
                              />
                            </td>
                            <td className="py-3 pr-4">
                              <Input
                                type="number"
                                min={0}
                                value={row.buyingPrice}
                                onChange={(e) =>
                                  updateVariantField(key, "buyingPrice", e.target.value)
                                }
                                className="w-24"
                              />
                            </td>
                            <td className="py-3 pr-4">
                              <Input
                                type="number"
                                min={0}
                                value={row.sellingPrice}
                                onChange={(e) =>
                                  updateVariantField(key, "sellingPrice", e.target.value)
                                }
                                className="w-24"
                              />
                            </td>
                            <td className="py-3 pr-4">
                              <Input
                                type="number"
                                min={0}
                                value={row.mrp}
                                onChange={(e) => updateVariantField(key, "mrp", e.target.value)}
                                className="w-24"
                              />
                            </td>
                            <td className="py-3 pr-4">
                              <Input
                                type="number"
                                min={0}
                                value={row.minStock}
                                onChange={(e) =>
                                  updateVariantField(key, "minStock", e.target.value)
                                }
                                className="w-20"
                              />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
              {errors.variants && (
                <p className="mt-2 text-sm text-red-500">{errors.variants}</p>
              )}
            </SectionCard>
          </>
        )}

        {/* GST — always last */}
        <SectionCard step={productType === "single" ? 5 : 5} title="GST">
          <div className="grid max-w-md grid-cols-2 gap-4">
            <FilterSelect
              variant="boxed"
              label="GST rate"
              value={gstRate}
              onChange={setGstRate}
              allLabel="Select GST rate"
              required
              options={GST_OPTIONS}
            />
            <Input
              label="HSN Code"
              optional
              placeholder="e.g. 6109"
              value={hsnCode}
              onChange={(e) => setHsnCode(e.target.value)}
            />
          </div>
        </SectionCard>

        {/* Actions */}
        <div className="flex items-center justify-end gap-3">
          <Button variant="outline" onClick={() => navigate("/app/product/list")} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} isLoading={loading} disabled={loading}>
            {isEdit ? "Save changes" : "Add product"}
          </Button>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default ProductForm;
